import * as React from 'react';

import { Menu } from 'lucide-react';

import { useAuth0 } from '@auth0/auth0-react';
import { Box, Drawer } from '@mui/material';

import { useUser } from '@itp/talent';
import { IconButton } from '@itp/talent/components/core/button';
import { useLocalAuth } from '@itp/talent/context/localAuthContext';
import { useMenuType } from '@itp/talent/hooks/useMenuType';
import { SHADCN_COLORS } from '@itp/talent/theme';

import { AsideNavigation } from '../../navigation';
import { AsideNavigationType } from '../../navigation/asideNavigation/asideNavigation';

const DRAWER_WIDTH = 280;

export const MobileMenu: React.FC = () => {
  const routeMenuType = useMenuType();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState<AsideNavigationType | null>(null);
  const { isAuthenticated: isAuth0Authenticated } = useAuth0();
  const { isAuthenticated: isLocalAuthenticated } = useLocalAuth();
  const isAuthenticated = isAuth0Authenticated || isLocalAuthenticated;
  const { user } = useUser();

  const container = typeof window !== 'undefined' ? () => window.document.body : undefined;

  const currentMenuType = activeSection ?? routeMenuType;

  const handleOpen = () => {
    setActiveSection(null);
    setMobileOpen(true);
  };

  if (!isAuthenticated && !routeMenuType) return null;

  const isAdmin = user.isAdmin || user.isImpersonate;

  const sections: { key: AsideNavigationType; label: string }[] = [
    { key: 'talent', label: 'Talent' },
    { key: 'company', label: 'Company' },
    ...(isAdmin ? [{ key: 'admin' as AsideNavigationType, label: 'Admin' }] : []),
  ];

  return (
    <>
      <IconButton
        aria-label="Mobile menu"
        color="inherit"
        edge="start"
        onClick={handleOpen}
        sx={{ display: { md: 'none' }, ml: 2, mr: -2 }}
      >
        <Menu size={20} />
      </IconButton>
      <Drawer
        slotProps={{
          paper: {
            sx: {
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              p: 1,
              width: DRAWER_WIDTH,
            },
          },
        }}
        anchor="right"
        container={container}
        ModalProps={{ keepMounted: true }}
        onClose={() => setMobileOpen(false)}
        open={mobileOpen}
        variant="temporary"
      >
        {isAuthenticated && (
          <Box
            sx={{
              backgroundColor: SHADCN_COLORS.muted,
              borderRadius: 1,
              display: 'flex',
              flexShrink: 0,
              gap: '2px',
              mb: 1,
              p: '3px',
            }}
          >
            {sections.map((section) => {
              const isActive = currentMenuType === section.key;
              return (
                <Box
                  sx={{
                    '&:hover': {
                      backgroundColor: isActive ? '#fff' : 'rgba(0,0,0,0.04)',
                    },
                    backgroundColor: isActive ? '#fff' : 'transparent',
                    borderRadius: 0.75,
                    boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
                    color: isActive ? 'text.primary' : 'text.secondary',
                    cursor: 'pointer',
                    flex: 1,
                    fontSize: '0.8125rem',
                    fontWeight: isActive ? 600 : 500,
                    px: 1,
                    py: 0.75,
                    textAlign: 'center',
                    transition: 'all 150ms ease',
                    userSelect: 'none',
                  }}
                  key={section.key}
                  onClick={() => setActiveSection(section.key)}
                >
                  {section.label}
                </Box>
              );
            })}
          </Box>
        )}
        {currentMenuType && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowX: 'hidden',
              overflowY: 'auto',
            }}
          >
            <AsideNavigation menuType={currentMenuType} />
          </Box>
        )}
      </Drawer>
    </>
  );
};
